import React, { useState } from 'react';
import { FiBell, FiCheck, FiTrash2, FiCheckCircle, FiAlertCircle, FiInfo, FiAlertTriangle, FiFilter, FiX, FiTrendingUp, FiAlertOctagon } from 'react-icons/fi';

type NotificationType = 'info' | 'success' | 'warning' | 'error' | 'opportunity' | 'conflict';
type NotificationCategory = 'system' | 'member' | 'action' | 'report';

interface Notification {
  id: string;
  type: NotificationType;
  category: NotificationCategory;
  title: string;
  message: string;
  timestamp: Date;
  read: boolean;
}

const mockNotifications: Notification[] = [
  {
    id: '1',
    type: 'info',
    category: 'member',
    title: 'Member Onboarding',
    message: 'Sarah Johnson completed onboarding, waiting for dashboard setup',
    timestamp: new Date(Date.now() - 1000 * 60 * 15),
    read: false,
  },
  {
    id: '2',
    type: 'success',
    category: 'member',
    title: 'Member Dashboard',
    message: 'Emma Davis Member dashboard is ready and accessible',
    timestamp: new Date(Date.now() - 1000 * 60 * 30),
    read: false,
  },
  {
    id: '3',
    type: 'conflict',
    category: 'member',
    title: 'Member Attention Required',
    message: 'Michael Chen Member requires immediate attention from advisor',
    timestamp: new Date(Date.now() - 1000 * 60 * 45),
    read: false,
  },
  {
    id: '4',
    type: 'warning',
    category: 'member',
    title: 'VeraScore Alert',
    message: 'Emma Davis is 5 below the 62 VeraScore threshold',
    timestamp: new Date(Date.now() - 1000 * 60 * 60 * 2),
    read: false,
  },
  {
    id: '5',
    type: 'success',
    category: 'member',
    title: 'VeraScore Achievement',
    message: 'James Wilson is 5 above the 62 VeraScore threshold',
    timestamp: new Date(Date.now() - 1000 * 60 * 60 * 3),
    read: false,
  },
  {
    id: '6',
    type: 'warning',
    category: 'report',
    title: 'VS Insight',
    message: 'Avg. Decision Time is -16.7% lower in September 1-31, 2025 to previous month.',
    timestamp: new Date(Date.now() - 1000 * 60 * 60 * 5),
    read: true,
  },
  {
    id: '7',
    type: 'success',
    category: 'report',
    title: 'VS Insight',
    message: 'First-time Borrowers is +19.3% higher in September 1-31, 2025 to previous month.',
    timestamp: new Date(Date.now() - 1000 * 60 * 60 * 6),
    read: true,
  },
  {
    id: '8',
    type: 'success',
    category: 'action',
    title: 'Action Completed',
    message: 'Evaluated Credit Union Acquisition',
    timestamp: new Date(Date.now() - 1000 * 60 * 60 * 12),
    read: true,
  },
  {
    id: '9',
    type: 'success',
    category: 'action',
    title: 'Action Completed',
    message: 'Upgraded Fraud Detection Systems',
    timestamp: new Date(Date.now() - 1000 * 60 * 60 * 24),
    read: true,
  },
  {
    id: '10',
    type: 'success',
    category: 'action',
    title: 'Action Completed',
    message: 'Implement Leadership Development Program',
    timestamp: new Date(Date.now() - 1000 * 60 * 60 * 24 * 2),
    read: true,
  },
  {
    id: '11',
    type: 'success',
    category: 'action',
    title: 'Action Completed',
    message: 'Launched Green Lending Program',
    timestamp: new Date(Date.now() - 1000 * 60 * 60 * 24 * 3),
    read: true,
  },
  {
    id: '12',
    type: 'success',
    category: 'action',
    title: 'Action Completed',
    message: 'Counter Competitor Rate Campaign',
    timestamp: new Date(Date.now() - 1000 * 60 * 60 * 24 * 4),
    read: true,
  },
  {
    id: '13',
    type: 'success',
    category: 'action',
    title: 'Action Completed',
    message: 'Finalized Local Business Partnership',
    timestamp: new Date(Date.now() - 1000 * 60 * 60 * 24 * 5),
    read: true,
  },
  {
    id: '14',
    type: 'error',
    category: 'system',
    title: 'Sync Failed',
    message: 'Unable to sync data with external system',
    timestamp: new Date(Date.now() - 1000 * 60 * 60 * 24 * 6),
    read: true,
  },
];

const NotificationsPage: React.FC = () => {
  const [notifications, setNotifications] = useState<Notification[]>(mockNotifications);
  const [filterType, setFilterType] = useState<'all' | 'unread' | NotificationType>('all');
  const [filterCategory, setFilterCategory] = useState<'all' | NotificationCategory>('all');
  const [showFilters, setShowFilters] = useState(false);

  const getRelativeTime = (date: Date) => {
    const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
    
    if (seconds < 60) return 'Just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
    return date.toLocaleDateString();
  };

  const getNotificationIcon = (type: NotificationType) => {
    switch (type) {
      case 'success':
        return <FiCheckCircle className="text-green-600" size={20} />;
      case 'warning':
        return <FiAlertTriangle className="text-yellow-600" size={20} />;
      case 'error':
        return <FiAlertCircle className="text-red-600" size={20} />;
      case 'opportunity':
        return <FiTrendingUp className="text-indigo-600" size={20} />;
      case 'conflict':
        return <FiAlertOctagon className="text-orange-600" size={20} />;
      default:
        return <FiInfo className="text-blue-600" size={20} />;
    }
  };

  const getNotificationStyle = (type: NotificationType, read: boolean) => {
    const baseStyles = 'p-4 rounded-lg border transition-all cursor-pointer';
    const readOpacity = read ? 'opacity-60' : '';
    
    switch (type) {
      case 'success':
        return `${baseStyles} bg-green-50 border-green-200 hover:bg-green-100 ${readOpacity}`;
      case 'warning':
        return `${baseStyles} bg-yellow-50 border-yellow-200 hover:bg-yellow-100 ${readOpacity}`;
      case 'error':
        return `${baseStyles} bg-red-50 border-red-200 hover:bg-red-100 ${readOpacity}`;
      case 'opportunity':
        return `${baseStyles} bg-indigo-50 border-indigo-200 hover:bg-indigo-100 ${readOpacity}`;
      case 'conflict':
        return `${baseStyles} bg-orange-50 border-orange-200 hover:bg-orange-100 ${readOpacity}`;
      default:
        return `${baseStyles} bg-blue-50 border-blue-200 hover:bg-blue-100 ${readOpacity}`;
    }
  };

  const markAsRead = (id: string) => {
    setNotifications(notifications.map(n => 
      n.id === id ? { ...n, read: true } : n
    ));
  };

  const markAllAsRead = () => {
    setNotifications(notifications.map(n => ({ ...n, read: true })));
  };

  const deleteNotification = (id: string) => {
    setNotifications(notifications.filter(n => n.id !== id));
  };

  const clearAll = () => {
    setNotifications([]);
  };

  const filteredNotifications = notifications.filter(n => {
    if (filterType === 'unread' && n.read) return false;
    if (filterType !== 'all' && filterType !== 'unread' && n.type !== filterType) return false;
    if (filterCategory !== 'all' && n.category !== filterCategory) return false;
    return true;
  });

  const unreadCount = notifications.filter(n => !n.read).length;

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
            <div className="flex items-center gap-3 mb-4 sm:mb-0">
              <div className="p-3 bg-slate-100 rounded-lg">
                <FiBell className="text-slate-700" size={24} />
              </div>
              <div>
                <h1 className="text-2xl font-semibold text-slate-800">Notifications</h1>
                <p className="text-sm text-slate-600">
                  {unreadCount > 0 ? `${unreadCount} unread notification${unreadCount > 1 ? 's' : ''}` : 'All caught up!'}
                </p>
              </div>
            </div>

            <div className="hidden sm:flex items-center gap-2">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="px-3 py-2 text-sm font-medium text-slate-700 bg-slate-100 rounded-md hover:bg-slate-200 transition-colors flex items-center gap-2"
              >
                <FiFilter size={16} />
                Filters
              </button>
              {unreadCount > 0 && (
                <button
                  onClick={markAllAsRead}
                  className="px-3 py-2 text-sm font-medium text-slate-700 bg-slate-100 rounded-md hover:bg-slate-200 transition-colors flex items-center gap-2"
                >
                  <FiCheck size={16} />
                  Mark all read
                </button>
              )}
              {notifications.length > 0 && (
                <button
                  onClick={clearAll}
                  className="px-3 py-2 text-sm font-medium text-red-600 bg-red-50 rounded-md hover:bg-red-100 transition-colors flex items-center gap-2"
                >
                  <FiTrash2 size={16} />
                  Clear all
                </button>
              )}
            </div>
          </div>

          {/* Mobile Action Buttons */}
          <div className="sm:hidden flex flex-wrap items-center gap-2 mb-4">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="px-3 py-2 text-sm font-medium text-slate-700 bg-slate-100 rounded-md hover:bg-slate-200 transition-colors flex items-center gap-2"
            >
              <FiFilter size={16} />
              Filters
            </button>
            {unreadCount > 0 && (
              <button
                onClick={markAllAsRead}
                className="px-3 py-2 text-sm font-medium text-slate-700 bg-slate-100 rounded-md hover:bg-slate-200 transition-colors flex items-center gap-2"
              >
                <FiCheck size={16} />
                Mark all read
              </button>
            )}
            {notifications.length > 0 && (
              <button
                onClick={clearAll}
                className="px-3 py-2 text-sm font-medium text-red-600 bg-red-50 rounded-md hover:bg-red-100 transition-colors flex items-center gap-2"
              >
                <FiTrash2 size={16} />
                Clear all
              </button>
            )}
          </div>

          {showFilters && (
            <div className="mb-6 p-4 bg-slate-50 rounded-lg border border-slate-200">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-medium text-slate-700">Filter Notifications</h3>
                <button
                  onClick={() => setShowFilters(false)}
                  className="text-slate-400 hover:text-slate-600"
                >
                  <FiX size={18} />
                </button>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Status
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {['all', 'unread', 'info', 'success', 'warning', 'error', 'opportunity', 'conflict'].map((type) => (
                      <button
                        key={type}
                        onClick={() => setFilterType(type as any)}
                        className={`px-3 py-1 text-sm rounded-md transition-colors ${
                          filterType === type
                            ? 'bg-slate-700 text-white'
                            : 'bg-white text-slate-700 border border-slate-300 hover:bg-slate-100'
                        }`}
                      >
                        {type.charAt(0).toUpperCase() + type.slice(1)}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Category
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {['all', 'system', 'member', 'action', 'report'].map((category) => (
                      <button
                        key={category}
                        onClick={() => setFilterCategory(category as any)}
                        className={`px-3 py-1 text-sm rounded-md transition-colors ${
                          filterCategory === category
                            ? 'bg-slate-700 text-white'
                            : 'bg-white text-slate-700 border border-slate-300 hover:bg-slate-100'
                        }`}
                      >
                        {category.charAt(0).toUpperCase() + category.slice(1)}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {filteredNotifications.length === 0 ? (
            <div className="text-center py-12">
              <div className="inline-block p-4 bg-slate-100 rounded-full mb-4">
                <FiBell className="text-slate-400" size={48} />
              </div>
              <h3 className="text-lg font-medium text-slate-700 mb-2">No notifications</h3>
              <p className="text-slate-500">
                {notifications.length === 0
                  ? "You're all caught up! No new notifications."
                  : "No notifications match your current filters."}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredNotifications.map((notification) => (
                <div
                  key={notification.id}
                  className={getNotificationStyle(notification.type, notification.read)}
                  onClick={() => !notification.read && markAsRead(notification.id)}
                >
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 mt-0.5">
                      {getNotificationIcon(notification.type)}
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-semibold text-slate-800">
                              {notification.title}
                            </h3>
                            {!notification.read && (
                              <span className="w-2 h-2 bg-blue-600 rounded-full"></span>
                            )}
                          </div>
                          <p className="text-sm text-slate-600 mb-2">
                            {notification.message}
                          </p>
                          <div className="flex items-center gap-3">
                            <span className="text-xs text-slate-500">
                              {getRelativeTime(notification.timestamp)}
                            </span>
                            <span className="text-xs text-slate-500 px-2 py-0.5 bg-white rounded-md border border-slate-200">
                              {notification.category}
                            </span>
                          </div>
                        </div>
                        
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteNotification(notification.id);
                          }}
                          className="flex-shrink-0 p-1 text-slate-400 hover:text-red-600 transition-colors"
                          aria-label="Delete notification"
                        >
                          <FiTrash2 size={16} />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NotificationsPage;
